import { imageStudioClient, fileToBase64 } from "./imageStudioClient";

function base64ToFile(base64, mimeType, fileName) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], fileName || "image-no-bg.png", {
    type: mimeType || "image/png",
  });
}

function toFile(imageSource) {
  if (imageSource instanceof File) return imageSource;
  if (imageSource instanceof Blob) {
    return new File([imageSource], "image.jpg", {
      type: imageSource.type || "image/jpeg",
    });
  }
  throw new Error("Unsupported image source for background removal");
}

/**
 * Remove image background via the AI gateway (server-side ONNX).
 *
 * @param {Blob|File} imageSource
 * @param {{ onProgress?: (percent: number) => void }} [options]
 * @returns {Promise<File>}
 */
export async function removeImageBackground(imageSource, { onProgress } = {}) {
  if (!imageSource) {
    throw new Error("No image to process");
  }

  const file = toFile(imageSource);
  const base64 = await fileToBase64(file);
  if (onProgress) onProgress(10);

  let progress = 10;
  const tick =
    onProgress &&
    setInterval(() => {
      progress = Math.min(90, progress + 5);
      onProgress(progress);
    }, 1200);

  try {
    const result = await imageStudioClient.removeBackground({
      imageBase64: base64,
      mimeType: file.type || "image/jpeg",
    });

    if (!result?.success || !result?.data?.base64) {
      throw new Error(result?.error || "Background removal failed");
    }

    if (onProgress) onProgress(100);
    const baseName = (file.name || "image").replace(/\.[^.]+$/, "") || "image";
    return base64ToFile(
      result.data.base64,
      result.data.mimeType || "image/png",
      `${baseName}-no-bg.png`
    );
  } catch (err) {
    const apiError = err?.body?.error;
    throw new Error(apiError || err?.message || "Background removal failed");
  } finally {
    if (tick) clearInterval(tick);
  }
}
